export interface DecimalLike {
    toString: () => string;
}

export interface PositionAmountDisplayInput {
    shouldFetchLiveUsdv: boolean;
    liveUsdv: DecimalLike | null;
    hasFetchedLiveUsdv: boolean;
    isLoadingLiveUsdv: boolean;
    balanceError: Error | null;
    fallbackUsdvAmount: string | null | undefined;
}

// Program Surfaces whose USDv is held directly at an address, so the live
// on-chain balance is the position balance. "ADDRESS" is current;
// "TREASURY"/"WALLET" are older payload aliases still accepted on read.
const DIRECT_USDV_BALANCE_PROGRAM_SURFACE_TYPES = new Set(["ADDRESS", "TREASURY", "WALLET"]);

export function normalizeProgramSurfaceType(type: string | null | undefined): string | null {
    const normalized = type
        ?.trim()
        .toUpperCase()
        .replace(/[\s-]+/g, "_");
    return normalized && normalized.length > 0 ? normalized : null;
}

export function resolveProgramSurfaceType(
    primaryType: string | null | undefined,
    fallbackType?: string | null
): string | null {
    return normalizeProgramSurfaceType(primaryType) ?? normalizeProgramSurfaceType(fallbackType);
}

/**
 * Whether a position holds USDv directly at an address (so its live on-chain
 * balance is the position balance) vs. a surface tracked some other way.
 *
 * Some position payloads omit `programSurface.type` but send
 * `programSurface.surface` (e.g. "Address"), so both signals are honored.
 */
export function shouldFetchLiveUsdvBalance(
    type: string | null | undefined,
    fallbackType?: string | null,
    surface?: string | null
): boolean {
    const signals = [resolveProgramSurfaceType(type, fallbackType), normalizeProgramSurfaceType(surface)];
    return signals.some((signal) => signal !== null && DIRECT_USDV_BALANCE_PROGRAM_SURFACE_TYPES.has(signal));
}

export function parseUsdvAmount(value: string | null | undefined): number {
    const parsed = Number.parseFloat(value ?? "0");
    return Number.isFinite(parsed) ? parsed : 0;
}

export function formatUsdAmount(value: number, fractionDigits = 2): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(value);
}

export function formatPositionDisplayAmount({
    shouldFetchLiveUsdv,
    liveUsdv,
    hasFetchedLiveUsdv,
    isLoadingLiveUsdv,
    balanceError,
    fallbackUsdvAmount
}: PositionAmountDisplayInput): string {
    const fallbackDisplay = formatUsdAmount(parseUsdvAmount(fallbackUsdvAmount));

    if (!shouldFetchLiveUsdv) {
        return fallbackDisplay;
    }

    if (liveUsdv !== null) {
        return formatUsdAmount(parseUsdvAmount(liveUsdv.toString()));
    }

    if (balanceError) {
        // The live balance couldn't be read. `manualUsdvAmount` is not
        // maintained, so show a dash rather than imply a real $0.00 balance.
        return "—";
    }

    if (hasFetchedLiveUsdv) {
        return formatUsdAmount(0);
    }

    return isLoadingLiveUsdv ? "..." : fallbackDisplay;
}
